import {
  appendQuery,
  buildCellServiceUrl,
  type CellServiceQuery,
} from './cell-session';
import { isAbortError, withTokenAuthRecovery } from './cors-auth-recovery';
import {
  assertManualUploadKeyMatchesFileName,
  resolvePresignedGetUrl,
  sanitizePresignHttpUrls,
} from './functions';
import type { Source } from './types';

// =============================================================================
// Client base
// =============================================================================
//
// Direct browser-side calls to ``https://{cellId}.api.tranquility.ai``
// ``/edge-lambda-service/*``. This replaces the Next.js BFF proxy routes:
//
//   /api/protected/intercom        → /intercom/token  +  /telemetry/events
//   /api/protected/sharedData      → /tools-drawer
//   /api/protected/evidence        → /evidence
//   /api/protected/cases/[…]       → /cases/inferred/list
//   /api/protected/download        → /download
//   /api/protected/heliotrace/[…]  → /heliotrace/create-case
//   /api/protected/cases/create    → /cases
//   /api/protected/file-upload     → /file-upload
//   /api/protected/file-purge      → /file-purge
//   /api/protected/source-info/[…] → /source-info/{s3Key:path}
//   /api/protected/answer          → /chat/history/item
//
// All functions take a ``{ cellId, accessToken }`` session pair.  Use
// ``getGatewaySession()`` (from ``lib/gateway-fetch``) to resolve them from the
// current Amplify session when the caller does not already have them.

/** Path prefix for every edge-lambda-service endpoint. */
const API_PREFIX = '/edge-lambda-service';

/**
 * Error thrown by every direct-CORS call to edge-lambda-service. Carries the
 * upstream HTTP status (when available) and the raw response body.
 */
export class EdgeLambdaApiError extends Error {
  public readonly status: number | null;
  public readonly detail: string | null;

  constructor(message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'EdgeLambdaApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Build a fully-qualified edge-lambda-service URL for a given cell.
 *
 * ``path`` is the route after ``/edge-lambda-service`` (e.g. ``"/evidence"``).
 * A leading slash is optional.
 */
export function buildEdgeLambdaUrl(cellId: string, path: string): string {
  return buildCellServiceUrl(cellId, API_PREFIX, path);
}

type EdgeLambdaSessionArgs = {
  /** ``custom:cell_id`` claim — used to build the per-cell API host. */
  cellId: string;
  /** Cognito access token. The pre-token Lambda injects all custom claims so
   * the Lambda Authorizer enforces per-user cell/tenant checks. */
  accessToken: string;
};

function assertSession(cellId: string, accessToken: string): void {
  if (!cellId) throw new EdgeLambdaApiError('Missing cellId (custom:cell_id claim).');
  if (!accessToken) throw new EdgeLambdaApiError('Missing access token.');
}

async function edgeRequest<T>(
  args: EdgeLambdaSessionArgs & {
    path: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    query?: CellServiceQuery;
    signal?: AbortSignal;
  },
): Promise<T> {
  const { cellId, path, method, body, query, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = appendQuery(buildEdgeLambdaUrl(cellId, path), query);
  const hasBody = body !== undefined;

  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        },
        body: hasBody ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (error) {
      // A caller-cancelled request is NOT a failure: rethrow the AbortError
      // untouched. Wrapping it would (a) break callers keying off
      // err.name === 'AbortError', surfacing spurious errors on unmount/refresh,
      // and (b) make the message match isLikelyCorsOrNetworkFailure, so
      // withTokenAuthRecovery would attempt a token refresh for a cancel — which
      // can bounce a tenant-switched employee to customer sign-in.
      if (isAbortError(error)) throw error;
      throw new EdgeLambdaApiError(
        `Network error calling edge-lambda-service ${path}: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new EdgeLambdaApiError(
        `edge-lambda-service ${path} returned ${res.status}`,
        res.status,
        detail || null,
      );
    }

    if (res.status === 204) {
      return undefined as unknown as T;
    }

    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch (error) {
      throw new EdgeLambdaApiError(
        `edge-lambda-service ${path} returned unparseable JSON (status ${res.status}): ${(error as Error).message}`,
        res.status,
      );
    }
    return parsed as T;
  });
}

// =============================================================================
// Intercom
// =============================================================================

/** Response from ``GET /intercom/token``. */
export interface IntercomTokenResponse {
  token: string | null;
  intercomUnavailable?: boolean;
}

/**
 * ``GET /intercom/token`` — fetch a signed Intercom identity token.
 *
 * Returns ``{ token: null, intercomUnavailable: true }`` when the Intercom
 * signing secret is not configured in the cell.
 */
export async function fetchIntercomToken(
  args: EdgeLambdaSessionArgs & { signal?: AbortSignal },
): Promise<IntercomTokenResponse> {
  const { cellId, signal } = args;
  assertSession(cellId, args.accessToken);

  const url = buildEdgeLambdaUrl(cellId, '/intercom/token');
  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
        signal,
      });
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw new EdgeLambdaApiError(
        `Network error calling edge-lambda-service /intercom/token: ${(error as Error).message}`,
      );
    }

    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new EdgeLambdaApiError(
        `edge-lambda-service /intercom/token returned ${res.status}`,
        res.status,
        detail || null,
      );
    }

    return (await res.json().catch(() => ({ token: null }))) as IntercomTokenResponse;
  });
}

// =============================================================================
// Telemetry events (replaces POST /api/protected/intercom → /telemetry/events)
// =============================================================================

export interface TelemetryEventBody {
  event_name: string;
  event_data: Record<string, unknown>;
}

/**
 * ``POST /telemetry/events`` — fire-and-forget usage event.
 *
 * Matches the shape the BFF forwarded: ``{ event_name, event_data }``.
 * (The old BFF accepted ``{ event_name, intercomEventData }`` and renamed the
 * field before forwarding — callers should now pass the renamed key directly.)
 */
export async function postTelemetryEvent(
  args: EdgeLambdaSessionArgs & { body: TelemetryEventBody; signal?: AbortSignal },
): Promise<{ event_key: string }> {
  return edgeRequest<{ event_key: string }>({
    ...args,
    path: '/telemetry/events',
    method: 'POST',
    body: args.body,
  });
}

// =============================================================================
// Feedback / telemetry events (analytics)
//
// Replaces the Next.js BFF proxy routes:
//   GET /api/protected/feedback            → GET /telemetry/events
//   GET /api/protected/feedback/[eventKey] → GET /telemetry/events/{eventKey}
//
// The analytics UI reads these directly from the browser like every other
// service, using the viewer's (admin) gateway session.
// =============================================================================

/** One page of feedback/telemetry events. Shape passed through from the cell. */
export interface FeedbackEventsPage {
  feedbackEvents: unknown[];
  totalFeedback: number;
  page: number;
  pageSize: number;
}

/**
 * ``GET /telemetry/events`` — one page of feedback/telemetry events.
 *
 * ``page`` is 1-based; the backend supports ``pageSize`` up to 200 and strips
 * heavy fields (screenshot/answer) from list rows.
 */
export async function fetchFeedbackEvents(
  args: EdgeLambdaSessionArgs & { page: number; pageSize: number; signal?: AbortSignal },
): Promise<FeedbackEventsPage> {
  const { page, pageSize, signal, cellId, accessToken } = args;
  return edgeRequest<FeedbackEventsPage>({
    cellId,
    accessToken,
    path: '/telemetry/events',
    method: 'GET',
    query: { page, page_size: pageSize },
    signal,
  });
}

/**
 * ``GET /telemetry/events/{eventKey}`` — a single feedback event, resolved
 * tenant-scoped by ``event_key`` alone (no owner user_id needed), returning the
 * full record including answer + screenshot.
 */
export async function fetchFeedbackEvent<T = unknown>(
  args: EdgeLambdaSessionArgs & { eventKey: string; signal?: AbortSignal },
): Promise<T> {
  const { eventKey, signal, cellId, accessToken } = args;
  return edgeRequest<T>({
    cellId,
    accessToken,
    path: `/telemetry/events/${encodeURIComponent(eventKey)}`,
    method: 'GET',
    signal,
  });
}

// =============================================================================
// Analytics — tenant-wide user sessions (TR-3018)
//
// Both endpoints 403 unless the caller's email is @tranquility-ai.com.
// =============================================================================

/** One question inside an analytics session. Text may be truncated in list rows. */
export interface AnalyticsSessionQuestion {
  question: string;
  epochTimeSecs: number;
  chatKey: string;
  requestType?: string | null;
  /** List previews only: the question text was cut to fit the response. */
  truncated?: boolean;
  /** Seconds from question to final answer (session detail only); null when unknown. */
  durationSecs?: number | null;
}

/** One chat session in the tenant, grouped from the chat-history archive. */
export interface AnalyticsSessionSummary {
  sessionId: string;
  chatKey: string;
  userId: string;
  caseId: string;
  /** All questions in the session; `questions` holds at most a preview of them. */
  questionCount: number;
  firstEpoch: number;
  lastEpoch: number;
  /** Oldest-first. */
  questions: AnalyticsSessionQuestion[];
}

export interface AnalyticsSessionsPage {
  /** Newest-first by last question. */
  sessions: AnalyticsSessionSummary[];
  totalSessions: number;
  /** Across all matching sessions, not just this page. */
  totalQuestions?: number;
  /** Distinct users across all matching sessions. */
  totalUsers?: number;
  page: number;
  pageSize: number;
  /** True when the backend hit its scan cap, so older sessions are missing. */
  truncated: boolean;
  /** Every user and case in range, ignoring the user/case filters (for dropdowns). */
  facets?: { users: string[]; cases: string[] };
}

/**
 * One session's questions, oldest-first. Answers are not included: load each via
 * ``fetchChatHistoryItem({ chatKey, epochTimeSecs })`` when it is expanded.
 */
export interface AnalyticsSessionDetail {
  sessionId: string;
  chatKey: string;
  /** Case tag the session was asked in (`*` = all cases); null when unknown. */
  caseId?: string | null;
  userId?: string | null;
  questions: AnalyticsSessionQuestion[];
}

/** ``GET /analytics/sessions`` — one page of the tenant's chat sessions. */
export async function fetchAnalyticsSessions(
  args: EdgeLambdaSessionArgs & {
    page: number;
    pageSize: number;
    user?: string | null;
    caseId?: string | null;
    /** Include Tranquility's own users; the list shows customers only by default. */
    includeInternal?: boolean;
    startDate?: string | null;
    endDate?: string | null;
    signal?: AbortSignal;
  },
): Promise<AnalyticsSessionsPage> {
  const { page, pageSize, user, caseId, includeInternal, startDate, endDate, signal, cellId, accessToken } =
    args;
  return edgeRequest<AnalyticsSessionsPage>({
    cellId,
    accessToken,
    path: '/analytics/sessions',
    method: 'GET',
    query: {
      page,
      page_size: pageSize,
      ...(user ? { user } : {}),
      ...(caseId ? { case: caseId } : {}),
      ...(includeInternal ? { include_internal: true } : {}),
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
    },
    signal,
  });
}

/**
 * ``GET /analytics/sessions/nearby`` — one user's sessions from 3 h before to
 * 15 min after ``atEpochSecs``. Keyed lookup when ``caseId`` is known.
 */
export async function fetchAnalyticsSessionsNearby(
  args: EdgeLambdaSessionArgs & {
    user: string;
    atEpochSecs: number;
    caseId?: string | null;
    signal?: AbortSignal;
  },
): Promise<{ sessions: AnalyticsSessionSummary[]; truncated: boolean }> {
  const { user, atEpochSecs, caseId, signal, cellId, accessToken } = args;
  return edgeRequest({
    cellId,
    accessToken,
    path: '/analytics/sessions/nearby',
    method: 'GET',
    query: { user, at: atEpochSecs, ...(caseId ? { case: caseId } : {}) },
    signal,
  });
}

/** ``GET /analytics/sessions/{sessionId}`` — one session's questions. */
export async function fetchAnalyticsSession(
  args: EdgeLambdaSessionArgs & { sessionId: string; signal?: AbortSignal },
): Promise<AnalyticsSessionDetail> {
  const { sessionId, signal, cellId, accessToken } = args;
  return edgeRequest<AnalyticsSessionDetail>({
    cellId,
    accessToken,
    path: `/analytics/sessions/${encodeURIComponent(sessionId)}`,
    method: 'GET',
    signal,
  });
}

/**
 * ``POST /analytics/attachments/sign`` — 5-minute download URL for any chat
 * attachment in the caller's tenant (Tranquility staff only). assistant-service's
 * sign endpoint only covers the caller's own attachments.
 */
export async function signAnalyticsAttachment(
  args: EdgeLambdaSessionArgs & { s3Key: string; filename?: string },
): Promise<{ signed_url: string }> {
  const { s3Key, filename, cellId, accessToken } = args;
  return edgeRequest<{ signed_url: string }>({
    cellId,
    accessToken,
    path: '/analytics/attachments/sign',
    method: 'POST',
    body: { s3Key, filename },
  });
}

// -----------------------------------------------------------------------------
// Analytics — source inspector ("What TimePilot sees", TR-3985)
//
// Every chunk TimePilot indexed for one user-visible file, plus the page text,
// transcript and keyframes those chunks came from. Tranquility staff only,
// like the rest of /analytics.
// -----------------------------------------------------------------------------

export type InspectKind =
  | 'document'
  | 'slides'
  | 'spreadsheet'
  | 'video'
  | 'audio'
  | 'image'
  | 'email'
  | 'container'
  | 'text'
  | 'other';

export type ChunkOriginKind =
  | 'page'
  | 'pageImage'
  | 'transcript'
  | 'videoAnalysis'
  | 'imageDescription'
  | 'spreadsheet'
  | 'email'
  | 'slides'
  | 'text'
  | 'other';

/** One indexed chunk and where in the file it came from. */
export interface InspectChunk {
  id: string;
  chunkIndex: number;
  text: string;
  tokenCount: number | null;
  /** 1-based page, for paged documents. */
  page: number | null;
  startTime: string | null;
  endTime: string | null;
  startSec: number | null;
  endSec: number | null;
  timestamps: string[] | null;
  slide: number | null;
  emailIndex: number | null;
  /** S3 key of the derived file the chunk was cut from. */
  originKey: string;
  originLabel: string;
  originKind: ChunkOriginKind;
}

/**
 * A paged file's page list, without per-page data: pages are loaded one at a
 * time with `fetchSourcePage`, so a 10,000-page document stays cheap to open.
 */
export interface InspectPageIndex {
  count: number;
  /** Where the page text came from. */
  textSource: 'textract' | 'custom_text' | null;
  /** 1-based pages with no stored text. */
  missingPages: number[];
  /** 1-based pages whose text is split into tiles. */
  tilePages: number[];
}

export interface InspectMedia {
  durationSec: number | null;
  /** Seconds between keyframes. */
  interval: number | null;
  /** Frames per video-analysis batch. */
  batchSize: number | null;
  frameCount: number;
  transcriptKey: string | null;
  videoAnalysisKey: string | null;
}

export interface InspectResponse {
  file: {
    url: string;
    name: string;
    md5: string | null;
    mimeType: string | null;
    resolvedVia: 'sidecar' | 'dispatch_record' | 'derived_location' | null;
    treeMd5s: string[];
  };
  kind: InspectKind;
  /**
   * Documents/slides: the cited page's chunks (or page 1's). Other kinds: every
   * chunk, or `[]` when there are too many. `chunksComplete` says which.
   */
  chunks: InspectChunk[];
  /** `chunks` is the file's whole chunk list; otherwise page it with `fetchSourceChunks`. */
  chunksComplete: boolean;
  cited: {
    chunkId: string | null;
    match: 'exact' | 'normalized' | 'substring' | null;
    page: number | null;
    startSec: number | null;
    endSec: number | null;
    /** The cited chunk itself, when the service found it. */
    chunk: InspectChunk | null;
  };
  pageIndex: InspectPageIndex | null;
  media: InspectMedia | null;
  children: { url: string; name: string; md5: string }[];
  intermediates: {
    key: string;
    label: string;
    kind: 'markdown' | 'text' | 'json' | 'csv' | 'transcript' | 'videoAnalysis';
  }[];
  warnings: string[];
  /** The chunk list hit the service's cap. */
  truncated: boolean;
}

export interface InspectSourceRequest {
  sourceFilePath?: string | null;
  dispatchedFilePath?: string | null;
  location?: string | null;
  citedText?: string | null;
  page?: number | null;
  sourceContext?: Record<string, unknown> | null;
}

function nonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function positivePage(v: unknown): number | null {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  const n = typeof v === 'number' ? Math.trunc(v) : parseInt(String(v), 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

/** Build the inspect request for a chat citation. */
export function inspectRequestFromSource(source: Source): InspectSourceRequest {
  const ctx =
    source.sourceContext && typeof source.sourceContext === 'object'
      ? (source.sourceContext as Record<string, unknown>)
      : null;
  // Evidence-locker previews carry a bucket-relative lookup path
  // (``data/bulk/...``) as sourceFilePath; the canonical ``s3://`` URL is on
  // the provenance file summary. Send the first real s3 URI, else the raw path.
  const sourceFilePath =
    [source.sourceFilePath, source.fileInfo?.fileUrl, source.sourceUrl].find(
      (v): v is string => typeof v === 'string' && v.trim().startsWith('s3://'),
    ) ?? nonEmptyString(source.sourceFilePath);
  return {
    sourceFilePath,
    dispatchedFilePath:
      nonEmptyString(source.dispatchedFilePath) ??
      nonEmptyString(ctx?.dispatchedFilePath) ??
      nonEmptyString(ctx?.dispatched_file_path),
    location: nonEmptyString(source.location),
    citedText: nonEmptyString(source.content),
    page:
      positivePage(source.pageNumber) ??
      positivePage(source.page) ??
      positivePage(ctx?.source_page_number),
    sourceContext: ctx,
  };
}

/**
 * The inspect request for one file picked from a citation's provenance
 * hierarchy: the container by ``sourceFilePath``, an extracted file by
 * ``dispatchedFilePath`` alone, or the cited file keeping its location, cited
 * text and page (the edge prefers ``dispatchedFilePath`` over ``sourceFilePath``).
 */
export function inspectRequestForHierarchyFile(
  source: Source,
  s3Url: string,
  kind: 'container' | 'cited' | 'extracted',
): InspectSourceRequest {
  if (kind === 'container') return { sourceFilePath: s3Url };
  if (kind === 'extracted') return { dispatchedFilePath: s3Url };
  return { ...inspectRequestFromSource(source), dispatchedFilePath: s3Url };
}

/** ``POST /analytics/sources/inspect`` — the indexed chunks of a citation's file. */
export async function inspectSource(
  args: EdgeLambdaSessionArgs & { request: InspectSourceRequest; signal?: AbortSignal },
): Promise<InspectResponse> {
  const { request, signal, cellId, accessToken } = args;
  return edgeRequest<InspectResponse>({
    cellId,
    accessToken,
    path: '/analytics/sources/inspect',
    method: 'POST',
    body: request,
    signal,
  });
}

export interface SourceTextResponse {
  key: string;
  text: string;
  size: number;
  /** The body was cut at the service's read cap. */
  truncated: boolean;
}

/** ``POST /analytics/sources/text`` — the text body of one derived file. */
export async function fetchSourceText(
  args: EdgeLambdaSessionArgs & { key: string; signal?: AbortSignal },
): Promise<SourceTextResponse> {
  const { key, signal, cellId, accessToken } = args;
  return edgeRequest<SourceTextResponse>({
    cellId,
    accessToken,
    path: '/analytics/sources/text',
    method: 'POST',
    body: { key },
    signal,
  });
}

export interface SourceFrame {
  index: number;
  ms: number;
  key: string;
  signedUrl: string;
}

export interface SourceFramesResponse {
  total: number;
  interval: number | null;
  frames: SourceFrame[];
}

/** One page of a paged file: its text, tiles, chunks and embedded images. */
export interface SourcePageResponse {
  page: number;
  /** Derived text for the page, readable via `fetchSourceText`. */
  textKey: string | null;
  tileKeys: string[];
  chunks: InspectChunk[];
  images: { key: string; md5: string; chunkCount: number }[];
  warnings: string[];
}

/** ``POST /analytics/sources/page`` — one page of a document or slide deck. */
export async function fetchSourcePage(
  args: EdgeLambdaSessionArgs & { md5: string; page: number; signal?: AbortSignal },
): Promise<SourcePageResponse> {
  const { md5, page, signal, cellId, accessToken } = args;
  return edgeRequest<SourcePageResponse>({
    cellId,
    accessToken,
    path: '/analytics/sources/page',
    method: 'POST',
    body: { md5, page },
    signal,
  });
}

export interface SourceChunksResponse {
  chunks: InspectChunk[];
  /** Pass back as `cursor` for the next page; null on the last page. */
  nextCursor: string | null;
  filesRead: number;
}

/** ``POST /analytics/sources/chunks`` — a cursor page of a file's indexed chunks. */
export async function fetchSourceChunks(
  args: EdgeLambdaSessionArgs & {
    md5: string;
    cursor?: string | null;
    limit?: number;
    signal?: AbortSignal;
  },
): Promise<SourceChunksResponse> {
  const { md5, cursor, limit, signal, cellId, accessToken } = args;
  return edgeRequest<SourceChunksResponse>({
    cellId,
    accessToken,
    path: '/analytics/sources/chunks',
    method: 'POST',
    body: { md5, cursor: cursor ?? null, limit },
    signal,
  });
}

/** ``POST /analytics/sources/frames`` — a page of a video's keyframes, with signed URLs. */
export async function fetchSourceFrames(
  args: EdgeLambdaSessionArgs & {
    md5: string;
    offset?: number;
    limit?: number;
    signal?: AbortSignal;
  },
): Promise<SourceFramesResponse> {
  const { md5, offset, limit, signal, cellId, accessToken } = args;
  return edgeRequest<SourceFramesResponse>({
    cellId,
    accessToken,
    path: '/analytics/sources/frames',
    method: 'POST',
    body: { md5, offset, limit },
    signal,
  });
}

/**
 * ``POST /analytics/sessions/{id}/feedback`` — thumbs-down one of the session's
 * turns on behalf of the customer who asked it (Tranquility staff only). The
 * edge Lambda reads the turn from the archive and records it as that customer's
 * negative feedback, tagged on-behalf-of-customer in Jira.
 */
export async function submitOnBehalfFeedback(
  args: EdgeLambdaSessionArgs & {
    sessionId: string;
    chatKey: string;
    epochTimeSecs: number;
    reason: string;
  },
): Promise<{ event_key: string }> {
  const { sessionId, chatKey, epochTimeSecs, reason, cellId, accessToken } = args;
  return edgeRequest<{ event_key: string }>({
    cellId,
    accessToken,
    path: `/analytics/sessions/${encodeURIComponent(sessionId)}/feedback`,
    method: 'POST',
    body: { chatKey, epochTimeSecs, reason },
  });
}

// =============================================================================
// Tools drawer (sharedData)
// =============================================================================

/**
 * ``POST /tools-drawer`` — shared captures / copies / notes / sources.
 *
 * The body is forwarded verbatim; the edge-lambda dispatches on
 * ``body.action`` (``"get"`` | ``"list"`` | ``"update"``). Tenant is resolved
 * from the Bearer access token (``custom:tenant_id`` / related claims); callers
 * must pass a valid session — do not send ``tenantFriendlyName`` in the body.
 * ``caseId`` is the storage case id (``*`` for all-cases); tenant prefix is not
 * embedded in ``caseId``.
 */
export async function fetchSharedData<T = unknown>(
  args: EdgeLambdaSessionArgs & { body: Record<string, unknown>; signal?: AbortSignal },
): Promise<T> {
  return edgeRequest<T>({
    ...args,
    path: '/tools-drawer',
    method: 'POST',
    body: args.body,
  });
}

// =============================================================================
// Evidence
// =============================================================================

export interface EvidenceBody {
  caseId: string;
  search?: string;
  fileTypes?: string[];
}

export interface EvidenceSource {
  url: string;
  mediaType?: string;
  [key: string]: unknown;
}

export interface EvidenceResponse {
  sources: EvidenceSource[];
}

/**
 * ``POST /evidence`` — list evidence sources for a case.
 *
 * Body: ``{ caseId, search?, fileTypes? }``
 * Response: ``{ sources: [{ url, mediaType }] }``
 */
export async function fetchEvidence(
  args: EdgeLambdaSessionArgs & { body: EvidenceBody; signal?: AbortSignal },
): Promise<EvidenceResponse> {
  return edgeRequest<EvidenceResponse>({
    ...args,
    path: '/evidence',
    method: 'POST',
    body: args.body,
  });
}

// =============================================================================
// Inferred cases
// =============================================================================

export interface InferredCasesResponse {
  options: string[];
}

/**
 * ``POST /cases/inferred/list`` — list inferred case tags for the caller's tenant.
 *
 * ``primary_only`` restricts to top-level cases; ``scope`` narrows to a subtree.
 */
export async function fetchInferredCases(
  args: EdgeLambdaSessionArgs & {
    primary_only?: boolean;
    tenant?: string | null;
    scope?: string | null;
    signal?: AbortSignal;
  },
): Promise<InferredCasesResponse> {
  const { primary_only = false, tenant = null, scope = null, signal, cellId, accessToken } = args;
  return edgeRequest<InferredCasesResponse>({
    cellId,
    accessToken,
    path: '/cases/inferred/list',
    method: 'POST',
    body: { primary_only, tenant, scope },
    signal,
  });
}

// =============================================================================
// Download (presigned URL)
// =============================================================================

export interface DownloadResponse {
  signedUrl: string;
  [key: string]: unknown;
}

/**
 * ``POST /download`` — generate a presigned S3 URL for a given file path.
 *
 * The caller should use ``window.open(signedUrl)`` or ``window.location.href``
 * for the download; the presigned URL is safe to navigate to from the browser
 * without CORS concerns.
 */
export async function fetchDownloadUrl(
  args: EdgeLambdaSessionArgs & {
    sourceFilePath: string;
    // Must be number | null. The API's Pydantic model is `int | None`; passing
    // a string (including "") fails request validation with 422 before the
    // handler runs. Callers must coerce upstream.
    page?: number | null;
    signal?: AbortSignal;
  },
): Promise<DownloadResponse> {
  const { sourceFilePath, page = null, signal, cellId, accessToken } = args;
  return edgeRequest<DownloadResponse>({
    cellId,
    accessToken,
    path: '/download',
    method: 'POST',
    body: { sourceFilePath, page },
    signal,
  });
}

// =============================================================================
// Heliotrace
// =============================================================================

/**
 * ``POST /cases`` — register a user-created case and write its Manual prefix.
 *
 * edge-lambda asks permission-service for ``folder_segment`` and writes
 * ``data/bulk/<folder_segment>/Manual/`` from that value. The name in the body
 * is the display title only. This route does not accept
 * ``use_name_as_folder_segment``; HelioTrace pins its own UUID on
 * ``POST /heliotrace/create-case``.
 */
export async function createUserCase(
  args: EdgeLambdaSessionArgs & {
    name?: string;
    personal?: boolean;
    openToAll?: boolean;
    signal?: AbortSignal;
  },
): Promise<Record<string, unknown>> {
  const { cellId, accessToken, signal, name, personal, openToAll } = args;
  const payload: Record<string, unknown> = {};
  if (personal) {
    payload.personal = true;
  } else {
    payload.name = name;
    if (openToAll) payload.open_to_all = true;
  }
  return edgeRequest<Record<string, unknown>>({
    cellId,
    accessToken,
    path: '/cases',
    method: 'POST',
    body: payload,
    signal,
  });
}

/**
 * ``POST /heliotrace/create-case`` — create the Manual bulk prefix for a case,
 * and optionally ``initial_migrant_entry_data.txt`` when a form snapshot (or
 * preformatted text) is included.
 *
 * Body: ``{ caseId, tenantFriendlyName?, initialVisitFormSnapshot?, initialMigrantEntryText? }``
 */
export async function createHeliotraceCase(
  args: EdgeLambdaSessionArgs & {
    body: {
      caseId: string;
      tenantFriendlyName?: string | null;
      initialVisitFormSnapshot?: Record<string, unknown> | null;
      initialMigrantEntryText?: string | null;
    };
    signal?: AbortSignal;
  },
): Promise<Record<string, unknown>> {
  return edgeRequest<Record<string, unknown>>({
    ...args,
    path: '/heliotrace/create-case',
    method: 'POST',
    body: args.body,
  });
}

// =============================================================================
// Manual case files (Ad Hoc bulk upload / purge)
// =============================================================================

export { AD_HOC_CASE_ID, AD_HOC_CASE_DISPLAY_NAME, LEGACY_AD_HOC_CASE_ID } from './ad-hoc-case';

export interface ManualCaseFileUploadResponse {
  uploadUrl: string;
  bucket: string;
  key: string;
  s3Url: string;
  expiresIn: number;
}

export interface ManualCaseFilePurgeResponse {
  deleted: string[];
  caseId: string;
}

export interface ManualFolderCreateResponse {
  caseId: string;
  folderName: string;
  key: string;
  s3Url: string;
}

/**
 * ``POST /api/protected/file-upload`` (BFF) → ``POST /edge-lambda-service/file-upload``.
 *
 * Only the caller's personal-files case (token email) is accepted by the
 * handler. Creates the ``data/bulk/<email>/Manual/`` prefix when missing,
 * then returns a presigned PUT URL. The BFF uploads the file bytes to S3
 * server-side (multipart form) so the browser avoids cross-origin PUTs to S3
 * that often fail with 403.
 */
export async function fetchManualCaseFileUploadUrl(
  args: {
    fileName: string;
    caseId: string;
    contentType?: string | null;
    signal?: AbortSignal;
  },
): Promise<ManualCaseFileUploadResponse> {
  const { fileName, caseId, contentType = null, signal } = args;

  let res: Response;
  try {
    res = await fetch('/api/protected/file-upload', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        caseId,
        fileName,
        ...(contentType ? { contentType } : {}),
      }),
      signal,
    });
  } catch (error) {
    throw new EdgeLambdaApiError(
      `Network error calling /api/protected/file-upload: ${(error as Error).message}`,
    );
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new EdgeLambdaApiError(
      `/api/protected/file-upload returned ${res.status}`,
      res.status,
      detail || null,
    );
  }

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch (error) {
    throw new EdgeLambdaApiError(
      `/api/protected/file-upload returned unparseable JSON (status ${res.status}): ${(error as Error).message}`,
      res.status,
    );
  }

  return sanitizePresignHttpUrls(parsed as ManualCaseFileUploadResponse);
}

/**
 * Presign via ``POST /file-upload``, then upload raw bytes through
 * ``POST /file-upload/put`` (server-side S3 PUT, no multipart).
 *
 * Returns the presign metadata (``s3Url``, ``key``, etc.) on success.
 */
export async function uploadManualCaseFile(
  args: EdgeLambdaSessionArgs & {
    file: Blob | File;
    fileName: string;
    caseId: string;
    contentType?: string | null;
    signal?: AbortSignal;
  },
): Promise<ManualCaseFileUploadResponse> {
  const { file, fileName, caseId, signal } = args;
  const contentType =
    args.contentType ??
    (file instanceof File && file.type ? file.type : null) ??
    'application/octet-stream';

  const presign = await fetchManualCaseFileUploadUrl({
    fileName,
    caseId,
    contentType,
    signal,
  });

  try {
    assertManualUploadKeyMatchesFileName(presign.key, fileName);
  } catch (error) {
    throw new EdgeLambdaApiError((error as Error).message, 400);
  }

  let res: Response;
  try {
    res = await fetch('/api/protected/file-upload/put', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': contentType,
        'X-TAI-Upload-Url': presign.uploadUrl,
        'X-TAI-Upload-Key': presign.key,
        'X-TAI-Upload-Bytes': String(file.size),
      },
      body: file,
      signal,
    });
  } catch (error) {
    throw new EdgeLambdaApiError(
      `Network error calling /api/protected/file-upload/put: ${(error as Error).message}`,
    );
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new EdgeLambdaApiError(
      `/api/protected/file-upload/put returned ${res.status}`,
      res.status,
      detail || null,
    );
  }

  return presign;
}

/**
 * ``DELETE /file-purge`` — delete manual delivery files under
 * ``data/bulk/<caseId>/Manual/``.
 *
 * Only the caller's personal-files case is accepted by the handler today.
 */
export async function purgeManualCaseFiles(
  args: EdgeLambdaSessionArgs & {
    fileNames: string[];
    caseId: string;
    signal?: AbortSignal;
  },
): Promise<ManualCaseFilePurgeResponse> {
  const { fileNames, caseId, signal, cellId, accessToken } = args;
  return edgeRequest<ManualCaseFilePurgeResponse>({
    cellId,
    accessToken,
    path: '/file-purge',
    method: 'DELETE',
    body: { caseId, fileNames },
    signal,
  });
}

/**
 * ``POST /file-upload/folder`` — create an S3 folder marker under
 * ``data/bulk/<caseId>/Manual/<folderName>/``.
 *
 * ``folderName`` is relative to ``Manual/`` and may include nested segments
 * (e.g. ``subdir/Notes``). Only the caller's personal-files case is accepted
 * by the handler today, matching ``/file-upload``.
 */
export async function createManualFolder(
  args: EdgeLambdaSessionArgs & {
    folderName: string;
    caseId: string;
    signal?: AbortSignal;
  },
): Promise<ManualFolderCreateResponse> {
  const { folderName, caseId, signal, cellId, accessToken } = args;
  return edgeRequest<ManualFolderCreateResponse>({
    cellId,
    accessToken,
    path: '/file-upload/folder',
    method: 'POST',
    body: { caseId, folderName },
    signal,
  });
}

// =============================================================================
// HelioTrace migrant media (photos / documents keyed by intake caseId)
// =============================================================================
//
// Distinct from the personal-files ``/file-upload`` + ``/file-purge`` above: these
// write under ``data/bulk/<caseId>/Manual/<folder>/`` for a real intake caseId.
// Presign is a direct cell-CORS call (like ``createHeliotraceCase``); raw bytes
// are PUT through the existing BFF ``/api/protected/file-upload/put`` route.

/** Subfolder under ``Manual/`` a migrant media object lives in. */
export type HeliotraceMediaFolder = 'photos' | 'documents';

export interface HeliotraceMediaPurgeResponse {
  deleted: string[];
  failed?: string[];
  caseId: string;
  folder: HeliotraceMediaFolder;
}

/**
 * ``POST /heliotrace/file-upload`` — presigned PUT URL for a migrant photo or
 * document under ``data/bulk/<caseId>/Manual/<folder>/<fileName>``.
 */
export async function fetchHeliotraceMediaUploadUrl(
  args: EdgeLambdaSessionArgs & {
    caseId: string;
    folder: HeliotraceMediaFolder;
    fileName: string;
    contentType?: string | null;
    signal?: AbortSignal;
  },
): Promise<ManualCaseFileUploadResponse> {
  const { cellId, accessToken, caseId, folder, fileName, contentType = null, signal } = args;
  const presign = await edgeRequest<ManualCaseFileUploadResponse>({
    cellId,
    accessToken,
    path: '/heliotrace/file-upload',
    method: 'POST',
    body: {
      caseId,
      folder,
      fileName,
      ...(contentType ? { contentType } : {}),
    },
    signal,
  });
  return sanitizePresignHttpUrls(presign);
}

/**
 * Presign via ``POST /heliotrace/file-upload``, then upload raw bytes through the
 * BFF ``POST /api/protected/file-upload/put`` (server-side S3 PUT, no multipart).
 *
 * Returns the presign metadata (``s3Url``, ``key``, etc.) on success.
 */
export async function uploadHeliotraceMediaFile(
  args: EdgeLambdaSessionArgs & {
    file: Blob | File;
    fileName: string;
    caseId: string;
    folder: HeliotraceMediaFolder;
    contentType?: string | null;
    signal?: AbortSignal;
  },
): Promise<ManualCaseFileUploadResponse> {
  const { cellId, accessToken, file, fileName, caseId, folder, signal } = args;
  const contentType =
    args.contentType ??
    (file instanceof File && file.type ? file.type : null) ??
    'application/octet-stream';

  const presign = await fetchHeliotraceMediaUploadUrl({
    cellId,
    accessToken,
    caseId,
    folder,
    fileName,
    contentType,
    signal,
  });

  try {
    assertManualUploadKeyMatchesFileName(presign.key, fileName);
  } catch (error) {
    throw new EdgeLambdaApiError((error as Error).message, 400);
  }

  let res: Response;
  try {
    res = await fetch('/api/protected/file-upload/put', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': contentType,
        'X-TAI-Upload-Url': presign.uploadUrl,
        'X-TAI-Upload-Key': presign.key,
        'X-TAI-Upload-Bytes': String(file.size),
      },
      body: file,
      signal,
    });
  } catch (error) {
    throw new EdgeLambdaApiError(
      `Network error calling /api/protected/file-upload/put: ${(error as Error).message}`,
    );
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new EdgeLambdaApiError(
      `/api/protected/file-upload/put returned ${res.status}`,
      res.status,
      detail || null,
    );
  }

  return presign;
}

/**
 * ``DELETE /heliotrace/file-purge`` — delete one or more migrant photos/documents
 * under ``data/bulk/<caseId>/Manual/<folder>/``.
 */
export async function purgeHeliotraceMediaFiles(
  args: EdgeLambdaSessionArgs & {
    caseId: string;
    folder: HeliotraceMediaFolder;
    fileNames: string[];
    signal?: AbortSignal;
  },
): Promise<HeliotraceMediaPurgeResponse> {
  const { cellId, accessToken, caseId, folder, fileNames, signal } = args;
  return edgeRequest<HeliotraceMediaPurgeResponse>({
    cellId,
    accessToken,
    path: '/heliotrace/file-purge',
    method: 'DELETE',
    body: { caseId, folder, fileNames },
    signal,
  });
}

export interface HeliotraceBrandingResponse {
  logoUrl: string | null;
  etag?: string | null;
}

/** Tenant-scoped HelioTrace settings returned by edge-lambda-service. */
export interface HeliotraceSettingsResponse {
  enabledLocales: string[];
}

/** Return the current tenant-scoped HelioTrace settings. */
export async function fetchHeliotraceSettings(
  args: EdgeLambdaSessionArgs & { signal?: AbortSignal },
): Promise<HeliotraceSettingsResponse> {
  return edgeRequest<HeliotraceSettingsResponse>({
    ...args,
    path: '/heliotrace/settings',
    method: 'GET',
  });
}

/** Replace the tenant's enabled HelioTrace locales. Requires tenant admin access. */
export async function updateHeliotraceSettings(
  args: EdgeLambdaSessionArgs & { enabledLocales: string[]; signal?: AbortSignal },
): Promise<HeliotraceSettingsResponse> {
  const { enabledLocales, ...session } = args;
  return edgeRequest<HeliotraceSettingsResponse>({
    ...session,
    path: '/heliotrace/settings',
    method: 'PUT',
    body: { enabledLocales },
  });
}

type HeliotraceLogoUploadResponse = ManualCaseFileUploadResponse;

/** Return the current tenant-scoped HelioTrace branding. */
export async function fetchHeliotraceBranding(
  args: EdgeLambdaSessionArgs & { signal?: AbortSignal },
): Promise<HeliotraceBrandingResponse> {
  return edgeRequest<HeliotraceBrandingResponse>({
    ...args,
    path: '/heliotrace/branding',
    method: 'GET',
  });
}

/**
 * Replace the current tenant's HelioTrace logo. The edge service owns a stable
 * S3 key; raw bytes still flow through the existing protected upload proxy.
 */
export async function uploadHeliotraceLogo(
  args: EdgeLambdaSessionArgs & {
    file: File;
    signal?: AbortSignal;
  },
): Promise<HeliotraceLogoUploadResponse> {
  const { cellId, accessToken, file, signal } = args;
  const presign = await edgeRequest<HeliotraceLogoUploadResponse>({
    cellId,
    accessToken,
    path: '/heliotrace/branding/logo-upload',
    method: 'POST',
    body: {
      contentType: file.type,
      size: file.size,
    },
    signal,
  });

  let res: Response;
  try {
    res = await fetch('/api/protected/file-upload/put', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': file.type,
        'X-TAI-Upload-Url': presign.uploadUrl,
        'X-TAI-Upload-Key': presign.key,
        'X-TAI-Upload-Bytes': String(file.size),
      },
      body: file,
      signal,
    });
  } catch (error) {
    throw new EdgeLambdaApiError(
      `Network error uploading HelioTrace logo: ${(error as Error).message}`,
    );
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new EdgeLambdaApiError(
      `HelioTrace logo upload returned ${res.status}`,
      res.status,
      detail || null,
    );
  }

  return presign;
}

/** Remove the tenant logo so HelioTrace falls back to the built-in mark. */
export async function deleteHeliotraceLogo(
  args: EdgeLambdaSessionArgs & { signal?: AbortSignal },
): Promise<HeliotraceBrandingResponse> {
  return edgeRequest<HeliotraceBrandingResponse>({
    ...args,
    path: '/heliotrace/branding/logo',
    method: 'DELETE',
  });
}

// =============================================================================
// Threat Report
// =============================================================================

/**
 * ``GET /threat-report/{caseId}`` — fetch the latest threat report row for a case.
 *
 * Response shape: ``{ status, epoch_time_secs, threat_report_json, ... }``
 *
 * ``caseFolderName`` is the case's S3 folder segment and is what actually selects
 * the row: edge-lambda rebuilds assistant-service's ``chat_key`` as
 * ``{caseFolderName || caseId}-{email}``, and assistant-service wrote it from the
 * folder segment. Omitting it on a UUID-folder case reads an empty partition and
 * 404s (TR-359). Safe to omit for a legacy case, whose segment is its display name.
 */
export async function fetchThreatReport(
  args: EdgeLambdaSessionArgs & {
    caseId: string;
    caseFolderName?: string | null;
    signal?: AbortSignal;
  },
): Promise<Record<string, unknown>> {
  const { caseId, caseFolderName, signal, cellId, accessToken } = args;
  const folderName = caseFolderName?.trim();
  return edgeRequest<Record<string, unknown>>({
    cellId,
    accessToken,
    path: `/threat-report/${encodeURIComponent(caseId)}`,
    query: folderName ? { caseFolderName: folderName } : undefined,
    method: 'GET',
    signal,
  });
}

/**
 * ``POST /threat-report`` — dispatch a threat-report action.
 *
 * Common actions: ``getSources``, ``getSelectors``, ``getThreatReport``,
 * ``deleteErrorThreatReport``, ``generateThreatReport``, etc. Response shape
 * varies by action.
 */
export async function postThreatReport(
  args: EdgeLambdaSessionArgs & { body: Record<string, unknown>; signal?: AbortSignal },
): Promise<Record<string, unknown>> {
  return edgeRequest<Record<string, unknown>>({
    ...args,
    path: '/threat-report',
    method: 'POST',
    body: args.body,
  });
}

export type ThreatReportSummaryStatus = 'completed' | 'inprogress' | 'error';

/** One generated threat report, without the report body. */
export interface ThreatReportSummary {
  chatKey: string;
  sessionId: string;
  epochTimeSecs: number;
  requestType: string;
  status: ThreatReportSummaryStatus;
  title: string;
  timestamp: string | null;
  fivecastDataDirectory: string | null;
  magnetDataDirectory: string | null;
  /** Selector ids the report was scoped to. Empty means every account in the export. */
  creatorIds: string[];
  /** Analyst who generated the report, the same identity a timeline stores as createdBy. */
  createdBy: string | null;
  /** True when this version is visible to the rest of the organization on the case. */
  shared: boolean;
  /**
   * BCP-47 locale this version was generated in, such as ``es-ES``.
   * Absent on rows written before language selection, which are English.
   */
  responseLanguage?: string | null;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function fallbackThreatReportTitle(epochTimeSecs: number): string {
  const iso = new Date(epochTimeSecs * 1000).toISOString();
  return `Threat Report — ${iso.slice(0, 19).replace('T', ' ')} UTC`;
}

function normalizeThreatReportStatus(value: unknown): ThreatReportSummaryStatus | null {
  if (value === 'completed' || value === 'inprogress' || value === 'error') return value;
  // A shared-list row from before status normalization has no status. It is a finished report.
  if (value == null || value === '') return 'completed';
  return null;
}

export function normalizeThreatReportSummary(raw: unknown): ThreatReportSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const epochTimeSecs = Number(row.epochTimeSecs ?? row.epoch_time_secs);
  if (!Number.isFinite(epochTimeSecs) || epochTimeSecs <= 0) return null;
  const title =
    asNullableString(row.title) ??
    asNullableString(row.threat_report_title) ??
    fallbackThreatReportTitle(epochTimeSecs);
  const status = normalizeThreatReportStatus(row.status);
  if (!status) return null;
  return {
    chatKey: asNullableString(row.chatKey ?? row.chat_key) ?? '',
    sessionId: asNullableString(row.sessionId ?? row.session_id) ?? '',
    epochTimeSecs,
    requestType: asNullableString(row.requestType ?? row.request_type) ?? 'threat_report',
    status,
    title,
    timestamp: asNullableString(row.timestamp),
    fivecastDataDirectory: asNullableString(
      row.fivecastDataDirectory ?? row.fivecast_data_directory ?? row.web_intelligence_data_directory,
    ),
    magnetDataDirectory: asNullableString(row.magnetDataDirectory ?? row.magnet_data_directory),
    creatorIds: creatorIdsFromSummary(row.creatorIds ?? row.creator_ids),
    createdBy: asNullableString(row.createdBy ?? row.userId ?? row.user_id),
    shared: row.shared === true,
    responseLanguage: asNullableString(row.responseLanguage ?? row.response_language),
  };
}

function creatorIdsFromSummary(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((id): id is string => typeof id === 'string')
    .map((id) => id.trim())
    .filter(Boolean);
}

/**
 * ``POST /threat-report`` ``listThreatReports`` — every report the caller
 * generated for a case, newest first. Summaries only; the body is loaded
 * separately with ``getThreatReport``.
 *
 * ``caseFolderName`` is the case's S3 folder segment and is what selects the
 * partition: edge-lambda reads ``{caseFolderName || caseId}-{email}``, the key
 * assistant-service wrote. Omitting it on a UUID-folder case lists an empty
 * partition, so a generated report never reaches Work Products. Safe to omit
 * for a legacy case, whose segment is its display name.
 */
export async function listThreatReports(
  args: EdgeLambdaSessionArgs & {
    caseId: string;
    caseFolderName?: string | null;
    signal?: AbortSignal;
  },
): Promise<ThreatReportSummary[]> {
  const { caseId, caseFolderName, signal, cellId, accessToken } = args;
  const data = await edgeRequest<{ reports?: unknown }>({
    cellId,
    accessToken,
    path: '/threat-report',
    method: 'POST',
    body: {
      action: 'listThreatReports',
      caseId,
      ...(caseFolderName ? { caseFolderName } : {}),
    },
    signal,
  });
  const reports = Array.isArray(data.reports) ? data.reports : [];
  return reports
    .map(normalizeThreatReportSummary)
    .filter((report): report is ThreatReportSummary => report !== null);
}

/**
 * ``GET /threat-report/{caseId}/shared`` — reports other people on this case
 * have shared, plus the caller's own shared versions. Same summary shape as
 * ``listThreatReports``. Snake-case fields are accepted so a list served
 * before that shape ships still renders.
 */
export async function listSharedThreatReports(
  args: EdgeLambdaSessionArgs & { caseId: string; signal?: AbortSignal },
): Promise<ThreatReportSummary[]> {
  const { caseId, signal, cellId, accessToken } = args;
  const data = await edgeRequest<{ reports?: unknown }>({
    cellId,
    accessToken,
    path: `/threat-report/${encodeURIComponent(caseId)}/shared`,
    method: 'GET',
    signal,
  });
  const reports = Array.isArray(data.reports) ? data.reports : [];
  return reports
    .map(normalizeThreatReportSummary)
    .filter((report): report is ThreatReportSummary => report !== null);
}

export async function setThreatReportShared(
  args: EdgeLambdaSessionArgs & {
    caseId: string;
    chatKey: string;
    epochTimeSecs: number;
    shared: boolean;
    signal?: AbortSignal;
  },
): Promise<void> {
  const { caseId, chatKey, epochTimeSecs, shared, signal, cellId, accessToken } = args;
  await edgeRequest<Record<string, unknown>>({
    cellId,
    accessToken,
    path: '/threat-report',
    method: 'POST',
    body: {
      action: shared ? 'shareThreatReport' : 'unshareThreatReport',
      caseId,
      chatKey,
      epochTimeSecs,
    },
    signal,
  });
}

/**
 * A social-media account ("selector") found in a Fivecast export.
 *
 * Sourced from the ``fivecast_creators-mt`` DynamoDB table, which
 * ``tpl-dataprep``'s fivecast-export-processing container populates per export
 * (hash ``export_s3_uri``, range ``creator_id``).
 */
export interface ThreatReportCreator {
  creatorId: string;
  creatorName: string;
  /** Every handle the export saw for this account; often more than one. */
  creatorHandles: string[];
  platform: string;
  /** Number of activities attributed to this account in the export. */
  activityCount: number;
  creatorImageUrl?: string;
  creatorLink?: string;
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Normalize one raw selector row.
 *
 * The service reads a schemaless DynamoDB table, so any field can be absent on
 * older rows. Defaulting here keeps the grid's renderers and comparators from
 * having to guard every cell.
 */
function normalizeThreatReportCreator(raw: unknown): ThreatReportCreator | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const creatorId = asTrimmedString(row.creatorId ?? row.creator_id);
  if (!creatorId) return null;

  const rawHandles = row.creatorHandles ?? row.creator_handles;
  const creatorHandles = Array.isArray(rawHandles)
    ? rawHandles.map(asTrimmedString).filter(Boolean)
    : [];

  const rawCount = row.activityCount ?? row.activity_count;
  const activityCount = Number(rawCount);

  return {
    creatorId,
    creatorName: asTrimmedString(row.creatorName ?? row.creator_name),
    creatorHandles,
    platform: asTrimmedString(row.platform),
    activityCount: Number.isFinite(activityCount) ? activityCount : 0,
    creatorImageUrl: asTrimmedString(row.creatorImageUrl ?? row.creator_image_url) || undefined,
    creatorLink: asTrimmedString(row.creatorLink ?? row.creator_link) || undefined,
  };
}

/**
 * ``POST /threat-report`` with ``action: "getSelectors"`` — list the accounts
 * present in one Fivecast export so the analyst can scope a report to a subset
 * of them.
 *
 * ``source`` is the export's storage URL, exactly as it appears in the create
 * modal's Fivecast dropdown (the provenance ``fileUrl``), because that is the
 * partition key the selectors table is written under.
 *
 * Large exports are returned in ALB-safe pages (``nextStartIndex``). This
 * helper walks them and returns the concatenated, activity-sorted list.
 */
export async function fetchThreatReportCreators(
  args: EdgeLambdaSessionArgs & { caseId: string; source: string; signal?: AbortSignal },
): Promise<ThreatReportCreator[]> {
  const { cellId, accessToken, caseId, source, signal } = args;
  const creators: ThreatReportCreator[] = [];
  let startIndex: number | undefined;
  let pages = 0;

  for (;;) {
    if (pages >= 500) {
      throw new Error('Threat report selectors pagination exceeded the page limit');
    }
    pages += 1;
    const body: Record<string, unknown> = { action: 'getSelectors', caseId, source };
    if (startIndex !== undefined) body.startIndex = startIndex;

    const res = await edgeRequest<Record<string, unknown>>({
      cellId,
      accessToken,
      path: '/threat-report',
      method: 'POST',
      body,
      signal,
    });

    const rows = res?.selectors;
    if (!Array.isArray(rows)) {
      const detail =
        typeof res?.message === 'string' && res.message.trim()
          ? res.message.trim()
          : 'Threat report selectors response was invalid';
      throw new Error(detail);
    }
    creators.push(
      ...rows
        .map(normalizeThreatReportCreator)
        .filter((creator): creator is ThreatReportCreator => creator !== null),
    );

    const next = res.nextStartIndex;
    if (typeof next !== 'number' || !Number.isInteger(next)) {
      return creators;
    }
    if (next <= (startIndex ?? 0)) {
      throw new Error('Threat report selectors pagination did not advance');
    }
    startIndex = next;
  }
}

/** A finished DOCX export, ready to download. */
export interface ThreatReportDocxExport {
  /**
   * Short-lived presigned S3 URL for the generated document, already passed
   * through ``resolvePresignedGetUrl`` so it is safe to hand to the browser.
   *
   * Navigate to it (e.g. an anchor click) to start the download — do not
   * ``fetch`` it, as the S3 origin does not allow browser CORS. The filename
   * comes from the ``Content-Disposition`` S3 returns, because the ``download``
   * attribute is ignored on cross-origin URLs.
   */
  signedUrl: string;
  fileName: string;
}

/** Why ``exportThreatReportDocx`` gave up, for the message shown to the user. */
export type ThreatReportDocxExportFailure = 'failed' | 'timeout';

/** Thrown by ``exportThreatReportDocx`` when no document will be produced. */
export class ThreatReportDocxExportError extends Error {
  public readonly reason: ThreatReportDocxExportFailure;

  constructor(message: string, reason: ThreatReportDocxExportFailure) {
    super(message);
    this.name = 'ThreatReportDocxExportError';
    this.reason = reason;
  }
}

type DocxExportJobStatus =
  | { status: 'pending' }
  | { status: 'ready'; signedUrl: string; fileName: string }
  | { status: 'failed'; error?: string };

/** How often to ask whether a queued export has finished. */
export const DOCX_EXPORT_POLL_INTERVAL_MS = 2_000;
/**
 * When to stop polling. Past the export worker's 300 s timeout the service
 * itself reports the job failed (its stale window is 420 s); this only covers a
 * service that stops answering altogether.
 */
export const DOCX_EXPORT_DEADLINE_MS = 450_000;
/** Consecutive poll errors (network blips, 5xx) tolerated before giving up. */
const DOCX_EXPORT_MAX_POLL_ERRORS = 3;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Base64 of the gzip of ``text``, or ``null`` where the browser has no
 * ``CompressionStream``. Report HTML compresses well over 10x, which keeps a
 * large report under the ALB's 1 MB request cap.
 */
export async function gzipBase64(text: string): Promise<string | null> {
  if (typeof CompressionStream === 'undefined') return null;
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
  const bytes = new Uint8Array(await new Response(stream).arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Generate a DOCX of a threat report and return a presigned URL to download it.
 *
 * The conversion runs on a background worker (TR-5038): a large report takes
 * longer than the edge API's 30 s request timeout, so this queues the export
 * with ``startDocXExport`` and polls ``getDocXExport`` until the document is
 * ready. The document itself never passes through the API, which caps a Lambda
 * response at 1 MB.
 *
 * Throws ``ThreatReportDocxExportError`` when the export fails or never
 * finishes, and ``EdgeLambdaApiError`` when it cannot be started. The deadline
 * covers everything in flight, not only the gaps between polls, so a stalled
 * request cannot leave the export hanging.
 */
export async function exportThreatReportDocx(
  args: EdgeLambdaSessionArgs & { caseId: string; html: string; signal?: AbortSignal },
): Promise<ThreatReportDocxExport> {
  const { signal } = args;
  const controller = new AbortController();
  const onCallerAbort = () => controller.abort(signal?.reason);
  if (signal?.aborted) onCallerAbort();
  else signal?.addEventListener('abort', onCallerAbort, { once: true });
  let timedOut = false;
  const deadline = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, DOCX_EXPORT_DEADLINE_MS);

  // Aborting cancels the export's own fetches, but not every await under them:
  // token recovery's /api/init refresh takes no signal. Racing against the
  // abort makes the deadline and a caller cancel settle regardless.
  const aborted = new Promise<never>((_resolve, reject) => {
    // A caller signal that was already aborted aborted the controller above,
    // before this listener existed, and abort events do not replay.
    if (controller.signal.aborted) {
      reject(controller.signal.reason);
      return;
    }
    controller.signal.addEventListener('abort', () => reject(controller.signal.reason), {
      once: true,
    });
  });
  const run = runDocxExport({ ...args, signal: controller.signal });
  // The loser of the race still settles later; keep that from surfacing as an
  // unhandled rejection.
  run.catch(() => {});
  aborted.catch(() => {});

  try {
    return await Promise.race([run, aborted]);
  } catch (error) {
    if (timedOut) {
      throw new ThreatReportDocxExportError('DOCX export did not finish in time', 'timeout');
    }
    throw error;
  } finally {
    clearTimeout(deadline);
    signal?.removeEventListener('abort', onCallerAbort);
  }
}

async function runDocxExport(
  args: EdgeLambdaSessionArgs & { caseId: string; html: string; signal: AbortSignal },
): Promise<ThreatReportDocxExport> {
  const { caseId, html, signal, cellId, accessToken } = args;
  const htmlGzip = await gzipBase64(html);
  const { jobId } = await edgeRequest<{ jobId: string }>({
    cellId,
    accessToken,
    path: '/threat-report',
    method: 'POST',
    body: htmlGzip ? { action: 'startDocXExport', caseId, htmlGzip } : { action: 'startDocXExport', caseId, html },
    signal,
  });

  let pollErrors = 0;
  // Ends by returning, by throwing, or when exportThreatReportDocx's deadline
  // aborts ``signal``.
  for (;;) {
    await sleep(DOCX_EXPORT_POLL_INTERVAL_MS, signal);
    let job: DocxExportJobStatus;
    try {
      job = await edgeRequest<DocxExportJobStatus>({
        cellId,
        accessToken,
        path: '/threat-report',
        method: 'POST',
        body: { action: 'getDocXExport', jobId },
        signal,
      });
    } catch (error) {
      if (isAbortError(error)) throw error;
      // A 4xx means the job is gone or not ours; retrying cannot help.
      const status = error instanceof EdgeLambdaApiError ? error.status : null;
      if ((status !== null && status < 500) || ++pollErrors >= DOCX_EXPORT_MAX_POLL_ERRORS) {
        throw new ThreatReportDocxExportError(`DOCX export ${jobId} could not be checked`, 'failed');
      }
      continue;
    }
    pollErrors = 0;
    if (job.status === 'ready') {
      // Sanitized here rather than at each call site, matching the other
      // presigned GETs in this layer, so every caller gets a browser-safe URL.
      return { signedUrl: resolvePresignedGetUrl(job.signedUrl), fileName: job.fileName };
    }
    if (job.status === 'failed') {
      throw new ThreatReportDocxExportError(job.error || `DOCX export ${jobId} failed`, 'failed');
    }
  }
}

// =============================================================================
// Chat history — single archived item (replaces GET /api/protected/answer)
// =============================================================================
//
// Two-hop read, mirroring ``uploadManualCaseFile`` in reverse: the row (and, for
// a large answer, a presigned S3 GET) comes from a direct cell-CORS call, and the
// S3 leg is done by a small BFF route because the browser cannot do it.

/** Header carrying the presigned S3 GET URL to ``/api/protected/answer/get``. */
const ANSWER_URL_HEADER = 'X-TAI-Answer-Url';
/** Header carrying the expected answer size (server-side logging only). */
const ANSWER_BYTES_HEADER = 'X-TAI-Answer-Bytes';

/**
 * One row from ``GET /chat/history/item``.
 *
 * ``answer`` is the inline answer text for normal-sized answers. For an answer
 * over the backend's 768 KB inline ceiling, ``answer`` is ``""`` and
 * ``answer_url`` / ``answer_bytes`` are present instead — see
 * ``fetchArchivedAnswerText``. Every other field is passed through from
 * DynamoDB, so the row is indexable.
 */
export interface ChatHistoryItemResponse {
  answer?: string;
  /**
   * Short-lived (5 min) SigV4 presigned S3 GET for the answer body, returned
   * only when the answer is too large to inline.
   *
   * MUST be resolved through ``fetchArchivedAnswerText`` (the BFF), never
   * ``fetch``ed from the browser: the chat-answers bucket has NO CORS
   * configuration, so S3 answers 200 and the page cannot read the response.
   */
  answer_url?: string;
  /** Size in bytes of the object ``answer_url`` points at. */
  answer_bytes?: number;
  [key: string]: unknown;
}

/**
 * ``GET /chat/history/item`` — one archived chat turn by ``chatKey`` +
 * ``epochTimeSecs``.
 *
 * Query params are camelCase here (not the snake_case used by the telemetry
 * endpoints) because the FastAPI handler declares them that way:
 * ``get_chat_history_item(chatKey: str = Query(...), epochTimeSecs: float = Query(...))``.
 *
 * Authorization is enforced in the cell: the table is resolved from the caller's
 * tenant UUID, and within the tenant the caller must own the ``chatKey``
 * (``{caseId}-{email}``) or carry ``is_admin``.
 *
 * A large answer comes back as ``answer_url`` rather than inline. That handoff
 * exists because the cell's internal ALB discards any response over 1,048,576
 * bytes from a Lambda target and substitutes its own 502 — while the function
 * logs a clean success, so nothing in the service's telemetry shows a failure.
 * (Lambda's own 6 MB sync-invoke limit is not the binding one.) Callers that
 * need the text must follow ``answer_url`` via ``fetchArchivedAnswerText``.
 */
export async function fetchChatHistoryItem(
  args: EdgeLambdaSessionArgs & {
    chatKey: string;
    epochTimeSecs: number;
    signal?: AbortSignal;
  },
): Promise<ChatHistoryItemResponse> {
  const { chatKey, epochTimeSecs, signal, cellId, accessToken } = args;
  return edgeRequest<ChatHistoryItemResponse>({
    cellId,
    accessToken,
    path: '/chat/history/item',
    method: 'GET',
    query: { chatKey, epochTimeSecs },
    signal,
  });
}

/**
 * ``POST /api/protected/answer/get`` (BFF) → presigned S3 GET, server-side.
 *
 * The read-side mirror of ``/api/protected/file-upload/put``: the bytes move
 * through the BFF precisely because the browser cannot do the S3 leg. The
 * chat-answers bucket has no CORS configuration (``NoSuchCORSConfiguration``),
 * so a direct browser ``fetch`` of ``answer_url`` gets a 200 the page is not
 * allowed to read. The BFF runs on ECS (an ALB ``ip`` target, no response cap),
 * so it can return a payload of any size.
 *
 * The route responds ``application/json`` (the stored object is
 * ``…-answer.json``), but this returns the **unparsed string** on purpose: the
 * caller assigns it to ``item.answer``, and ``mapHistoryItemToAnswer`` does the
 * ``JSON.parse`` itself. That makes it byte-for-byte what the Lambda's inline
 * path would have produced (``resp["Body"].read().decode("utf-8")``), so large
 * and small answers reach the renderer through identical code.
 */
export async function fetchArchivedAnswerText(
  args: { answerUrl: string; answerBytes?: number | null; signal?: AbortSignal },
): Promise<string> {
  const { answerUrl, answerBytes = null, signal } = args;

  let res: Response;
  try {
    res = await fetch('/api/protected/answer/get', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        [ANSWER_URL_HEADER]: answerUrl,
        ...(answerBytes != null ? { [ANSWER_BYTES_HEADER]: String(answerBytes) } : {}),
      },
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new EdgeLambdaApiError(
      `Network error calling /api/protected/answer/get: ${(error as Error).message}`,
    );
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new EdgeLambdaApiError(
      `/api/protected/answer/get returned ${res.status}`,
      res.status,
      detail || null,
    );
  }

  return res.text();
}

// =============================================================================
// Query keys
// =============================================================================

export const edgeLambdaKeys = {
  all: ['edge-lambda'] as const,
  evidence: (cellId: string | null, caseId: string | null) =>
    [...edgeLambdaKeys.all, 'evidence', cellId, caseId] as const,
  inferredCases: (cellId: string | null) =>
    [...edgeLambdaKeys.all, 'inferredCases', cellId] as const,
  threatReportCreators: (cellId: string | null, caseId: string | null, source: string | null) =>
    [...edgeLambdaKeys.all, 'threatReportCreators', cellId, caseId, source] as const,
  // The folder segment is part of the key: the list is first fetched before the cases
  // list lands (segment unknown, display-name fallback) and must refetch once it does.
  // main.tsx invalidates on the ``[all, 'threatReportVersions']`` prefix, which still matches.
  threatReportVersions: (
    cellId: string | null,
    caseId: string | null,
    caseFolderName: string | null = null,
  ) => [...edgeLambdaKeys.all, 'threatReportVersions', cellId, caseId, caseFolderName] as const,
  threatReportShared: (cellId: string | null, caseId: string | null) =>
    [...edgeLambdaKeys.all, 'threatReportShared', cellId, caseId] as const,
};
